"use client"

import { useState, useEffect, useRef, useMemo } from "react"
import Select from "react-select"
import { useNavigate } from "react-router-dom";
import { api, getAuthHeaders } from "../utils/api";
import ConfirmModal from "../components/ConfirmModal";
import Loader from "../components/Loader";
import Layout from '../components/Layout';
import "../styles/my-assets-liability.scss"
import { getCurrencySymbol } from '../utils/getCurrencySymbol';
import { showSuccessToast, showErrorToast } from "../components/Toast";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { formatLargeNumber } from '../utils/formatNumber';
import CountrySelectReact from '../utils/CountrySelect';
import { getDialCodeFromUserProfile } from '../utils/localeDetection';
import { useSelector } from "react-redux";
import TruncatedTooltipText from "../components/TruncatedTooltipText";


const MyLiability = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isValueVisible, setIsValueVisible] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [liabilities, setLiabilities] = useState([]);
  const [liabilitiesSummary, setLiabilitiesSummary] = useState([]);
  const [listMeta, setListMeta] = useState({ totalPages: 1, totalResults: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeout = useRef();
  const navigate = useNavigate();
  // Confirm modal for delete
  const [deleteModal, setDeleteModal] = useState({ show: false, liabilityId: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Share functionality state
  const [isShareMode, setIsShareMode] = useState(false);
  const [selectedLiabilities, setSelectedLiabilities] = useState([]);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [showAddCollaboratorModal, setShowAddCollaboratorModal] = useState(false);
  const [showCollaboratorAddedModal, setShowCollaboratorAddedModal] = useState(false);
  const [showLiabilitiesSharedModal, setShowLiabilitiesSharedModal] = useState(false);
  const [newCollaboratorName, setNewCollaboratorName] = useState('');
  const [sharedLiabilitiesCount, setSharedLiabilitiesCount] = useState(0);
  const [sharedByMeResponse, setSharedByMeResponse] = useState(null);
  const [alreadySharedLiabilityIds, setAlreadySharedLiabilityIds] = useState([]);

  // Fetch liabilities from API
  const fetchLiabilities = async (search = searchTerm, pg = page) => {
    setLoading(true);
    setError(null);
    try {
      let url = `/liabilities?page=${pg}&limit=10&sortBy=createdAt:desc`;
      if (search && search.trim().length >= 3) {
        url = `/liabilities?search=${encodeURIComponent(search)}&page=${pg}&limit=10&sortBy=createdAt:desc`;
      }
      const response = await api.get(url, { headers: getAuthHeaders() });
      setLiabilities(response.data?.results || response.data || []);
      setLiabilitiesSummary(response.data?.summary || []);
      setListMeta({
        totalPages: response.data?.totalPages || 1,
        totalResults: response.data?.totalResults || (response.data?.results?.length || 0),
      });
    } catch (e) {
      setError(e?.message || "Failed to fetch liabilities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchTerm && searchTerm.trim().length >= 3) {
      searchTimeout.current = setTimeout(() => {
        fetchLiabilities(searchTerm, page);
      }, 500);
    } else {
      fetchLiabilities();
    }
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm, page]);

  // Fetch collaborators for share feature
  useEffect(() => {
    const fetchCollaborators = async () => {
      try {
        const response = await api.get('/sharing/collaborators?status=active&limit=20&page=1', { headers: getAuthHeaders() });
        // Map response to expected format for dropdown
        let collabList = Array.isArray(response.data?.results)
          ? response.data.results.map(item => ({
              id: item.collaboratorId?.id,
              name: item.collaboratorId?.name || "",
              email: item.collaboratorId?.email || "",
              mobileNumber: item.collaboratorId?.mobileNumber || "",
              firstName: item.collaboratorId?.firstName || "",
              lastName: item.collaboratorId?.lastName || ""
            }))
          : [];
        setCollaborators(collabList);
      } catch (e) {
        console.error('Failed to fetch collaborators:', e);
      }
    };
    fetchCollaborators();
  }, []);

  // Pagination controls
  const totalPages = listMeta.totalPages || 1;
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };


  // Toggle favorite status (API call if supported)
  const toggleFavorite = async (id) => {
    // Implement API call if backend supports favorite toggle
    setLiabilities(liabilities.map((liability) => (liability.id === id ? { ...liability, isFavorite: !liability.isFavorite } : liability)));
  };

  // Duplicate liability (API call if supported)
  const duplicateLiability = async (id) => {
    try {
      const response = await api.post(`/liabilities/${id}/clone`, {}, { headers: getAuthHeaders() });
      if (response && response.data) {
        setPage(1);
        fetchLiabilities(searchTerm, 1);
      }
    } catch (e) {
      // Extract error message from API response
      const errorMessage = e?.response?.data?.message || e?.message || 'Failed to duplicate liability.';
      setError(errorMessage);
    }
  };

  // Delete liability
  const handleDeleteClick = (liabilityId) => {
    setDeleteModal({ show: true, liabilityId });
  };
  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, liabilityId: null });
  };
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/liabilities/${deleteModal.liabilityId}`, { headers: getAuthHeaders() });
      setDeleteModal({ show: false, liabilityId: null });
      setPage(1);
      fetchLiabilities(searchTerm, 1);
    } catch (e) {
      setError(e?.message || "Failed to delete liability.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      // The API allows at most 100 per page, so fetch page by page (capped at 50 pages)
      const liabilitiesData = [];
      let totalPages = 1;
      for (let pg = 1; pg <= totalPages && pg <= 50; pg++) {
        const response = await api.get(`/liabilities?page=${pg}&limit=100&sortBy=createdAt:desc`, { headers: getAuthHeaders() });
        const results = Array.isArray(response.data) ? response.data : response.data?.results;
        if (!Array.isArray(results)) break;
        liabilitiesData.push(...results);
        totalPages = Number(response.data?.totalPages) || 1;
      }
      if (liabilitiesData.length === 0) {
        showErrorToast('No liability data to export.');
        return;
      }
      // Prepare worksheet data
      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(liabilitiesData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Liabilities");
      XLSX.writeFile(workbook, "liabilities_export.xlsx");
    } catch (e) {
      showErrorToast(e?.message || 'Failed to export liabilities.');
    }
  };

  // Summary amounts are in the user's currency; older API versions only sent upcomingPaymentsCurrency (ML-15)
  const summaryCurrency = liabilitiesSummary?.currency || liabilitiesSummary?.upcomingPaymentsCurrency;

  // Format currency
  const formatCurrency = (value, currency) => {
    if (!isValueVisible) {
      return "••••••";
    }
    if (typeof value === "number" && !isNaN(value)) {
      return formatLargeNumber(value, currency);
    }
    return "-";
  };

  // Format updated date
  const formatUpdatedAt = (updatedAt) => {
    if (!updatedAt) return "";
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const diffMs = now - updatedDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays >= 1) {
      return ` ${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours >= 1) {
      return ` ${diffHours} hr ago`;
    } else {
      return ` ${diffMinutes} min ago`;
    }
  };

  // Format liability type for display
  const formatLiabilityType = (liabilityType) => {
    if (!liabilityType) return "General";

    // Map camelCase API values to readable display values
    const typeMap = {
      'creditCard': 'Credit Card',
      'vehicleLoan': 'Vehicle Loan',
      'generalLoan': 'Loan',
      'taxPayable': 'Tax Payable',
      'personalDebts': 'Personal Debts',
      'personalDebt': 'Personal Debt',
      'insurancePremium': 'Insurance Premium',
      'bnpl': 'BNPL',
      'buyNowPayLater': 'BNPL',
      'overdraft': 'Overdraft',
      'mortgage': 'Mortgage',
      // Handle space-separated versions too
      'credit card': 'Credit Card',
      'vehicle loan': 'Vehicle Loan',
      'loan': 'Loan',
      'tax payable': 'Tax Payable',
      'personal debts': 'Personal Debts',
      'insurance premium': 'Insurance Premium',
    };

    return typeMap[liabilityType] || liabilityType.charAt(0).toUpperCase() + liabilityType.slice(1);
  };

  // Share functionality handlers
  const handleShareClick = async () => {
    setIsShareMode(!isShareMode);
    setSelectedLiabilities([]);
    setSelectedCollaborator(null);
    setAlreadySharedLiabilityIds([]);
    
    // Fetch liabilities already shared by the logged-in user
    if (!isShareMode) {
      try {
        const response = await api.get('/sharing/liabilities/shared-by-me', {
          headers: getAuthHeaders()
        });
        setSharedByMeResponse(response.data);
      } catch (e) {
        console.error('Failed to fetch shared by me:', e);
        setSharedByMeResponse(null);
      }
    }
  };

  const isLiabilityAlreadyShared = (liabilityId) => {
    return alreadySharedLiabilityIds.includes(liabilityId);
  };

  const isLiabilityShareSelectionDisabled = (liability) => {
    if (!isShareMode) return false;
    if (liability?.isDraft) return true;
    return selectedCollaborator ? isLiabilityAlreadyShared(liability.id) : false;
  };

  const getShareableLiabilities = () =>
    liabilities.filter((liability) => !liability.isDraft && !isLiabilityAlreadyShared(liability.id));

  const handleLiabilitySelection = (liabilityId) => {
    const liability = liabilities.find((l) => l.id === liabilityId);
    if (!liability || isLiabilityShareSelectionDisabled(liability)) return;

    setSelectedLiabilities((prev) => {
      if (prev.includes(liabilityId)) {
        return prev.filter((id) => id !== liabilityId);
      } else {
        return [...prev, liabilityId];
      }
    });
  };

  const handleSelectAll = () => {
    const shareableLiabilities = getShareableLiabilities();

    if (selectedLiabilities.length === shareableLiabilities.length) {
      setSelectedLiabilities([]);
    } else {
      setSelectedLiabilities(shareableLiabilities.map((liability) => liability.id));
    }
  };

  const handleCollaboratorSelect = async (collaboratorId) => {
    setSelectedCollaborator(collaboratorId);
    
    // Find ALL liabilities shared with the selected collaborator
    // The API returns one entry per shared liability, so we need to filter all matching entries
    if (sharedByMeResponse?.results && collaboratorId) {
      console.log('Looking for collaborator:', collaboratorId);
      console.log('Total shared entries:', sharedByMeResponse.results.length);
      
      // Filter ALL results that match the collaborator ID
      const matchingResults = sharedByMeResponse.results.filter(
        item => String(item.collaboratorId?.id) === String(collaboratorId)
      );
      
      console.log('Matching entries found:', matchingResults.length);
      
      // Extract liability IDs from all matching entries
      const liabilityIds = matchingResults
        .map(item => item.liabilityId?.id)
        .filter(Boolean);
      
      console.log('Already shared liability IDs:', liabilityIds);
      setAlreadySharedLiabilityIds(liabilityIds);
    } else {
      setAlreadySharedLiabilityIds([]);
    }
  };

  const handleShareLiabilities = async () => {
    if (!selectedCollaborator) {
      showErrorToast('Please select a collaborator.');
      return;
    }
    if (selectedLiabilities.length === 0) {
      showErrorToast('Please select at least one liability.');
      return;
    }

    const shareableLiabilityIds = selectedLiabilities.filter((liabilityId) => {
      const liability = liabilities.find((l) => l.id === liabilityId);
      return liability && !liability.isDraft;
    });

    if (shareableLiabilityIds.length === 0) {
      showErrorToast('Draft liabilities cannot be shared.');
      return;
    }

    try {
      const permissions = {
        canViewDetails: true,
        canViewPhotos: true,
        canViewDocuments: false
      };
      const payload = {
        liabilityIds: shareableLiabilityIds,
        collaboratorId: selectedCollaborator || ' ',
        permissions
      };
      const response = await api.post('/sharing/liabilities', payload, { headers: getAuthHeaders() });
      if (response) {
        setSharedLiabilitiesCount(selectedLiabilities.length);
        setShowLiabilitiesSharedModal(true);
        setIsShareMode(false);
        setSelectedLiabilities([]);
        setSelectedCollaborator(null);
        showSuccessToast('Liabilities shared successfully!');
      } else {
        showErrorToast('Failed to share liabilities.');
      }
    } catch (e) {
      let errorMsg = 'Failed to share liabilities.';
      if (e?.response?.data?.message) {
        errorMsg = e.response.data.message;
      } else if (e?.message) {
        errorMsg = e.message;
      }
      showErrorToast(errorMsg);
    }
  };
  // Add Collaborator Modal Component
  const AddCollaboratorModal = ({ show, onClose, onSubmit }) => {
    // Get current user's data from Redux (must be called before any early returns)
    const userData = useSelector((state) => state.user?.data);
    
    // Extract owner's email - check top level and all identities
    const ownerEmails = [
      userData?.email,
      ...(userData?.identities || []).map(id => id.identifier).filter(id => id?.includes('@'))
    ].map(e => e?.toLowerCase()).filter(Boolean);
    
    // Extract owner's phone - check top level and identities array for SMS provider
    const smsIdentity = userData?.identities?.find(id => id.provider === 'sms');
    const ownerPhoneRaw = smsIdentity?.identifier || userData?.phone || userData?.mobileNumber || userData?.mobile || "NA";
    const ownerPhoneDigits = ownerPhoneRaw.replace(/\D/g, "");
    const defaultCountryCode = useMemo(
      () => getDialCodeFromUserProfile(userData),
      [userData]
    );

    if (!show) return null;

    const validationSchema = Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email")
        .required("Email is required")
        .test("not-owner-email", "You cannot add yourself as a collaborator", function(value) {
          if (!value || ownerEmails.length === 0) return true;
          const enteredEmail = value.toLowerCase();
          // Check if entered email matches any of owner's emails
          return !ownerEmails.includes(enteredEmail);
        }),
      countryCode: Yup.string().required("Country code is required"),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required")
        .test("not-owner-phone", "You cannot add yourself as a collaborator", function(value) {
          if (!value || !ownerPhoneDigits || ownerPhoneDigits === "NA" || ownerPhoneDigits.length < 10) return true;
          // Access countryCode from parent object (sibling field)
          const countryCode = this.parent?.countryCode || "";
          const countryCodeDigits = countryCode.replace(/\D/g, "");
          // Build entered phone number
          const enteredPhone = countryCodeDigits + value;
          // Check if entered phone matches owner's phone
          return !enteredPhone.endsWith(ownerPhoneDigits);
        }),
      notes: Yup.string(),
    });

    const initialValues = {
      firstName: "",
      lastName: "",
      email: "",
      countryCode: defaultCountryCode,
      mobile: "",
      notes: "",
    };

    return (
      <div className="share-modal-overlay" onClick={onClose}>
        <div className="share-collaborator-modal" onClick={(e) => e.stopPropagation()}>
          <button className="share-modal-close-btn" onClick={onClose}>
            <i className="bi bi-x"></i>
          </button>

          <div className="share-modal-header">
            <h5 className="share-modal-title">Who are you adding as your collaborator?</h5>
            <p className="share-modal-subtitle">
              It can be your spouse/partner, family member, or anyone you trust.
              you have full control over what you share with them.
            </p>
          </div>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={(formValues, formikHelpers) => {
              const payload = {
                email: formValues.email,
                name: `${formValues.firstName} ${formValues.lastName}`.trim(),
                phone: `${formValues.countryCode}${formValues.mobile}`.replace(/\s+/g, ''),
                permissions: {
                  canViewBasicInfo: true,
                  canViewDescription: true,
                  canViewPhotos: false,
                  canViewDocuments: false
                },
                notes: formValues.notes || ""
              };
              onSubmit(payload, formikHelpers);
            }}
          >
            {({ isSubmitting, values, errors, touched }) => (
              <Form className="share-collaborator-form">
                <div className="share-form-row">
                  <div className="share-form-group">
                    <label htmlFor="firstName">First Name*</label>
                    <Field
                      type="text"
                      id="firstName"
                      name="firstName"
                      className="form-control"
                      placeholder="John"
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="error-text"
                    />
                  </div>

                  <div className="share-form-group">
                    <label htmlFor="lastName">Last Name*</label>
                    <Field
                      type="text"
                      id="lastName"
                      name="lastName"
                      className="form-control"
                      placeholder="Doe"
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="error-text"
                    />
                  </div>
                </div>

                <div className="share-form-row">
                  <div className="share-form-group">
                    <label htmlFor="email">Email*</label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="john.doe@gmail.com"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="error-text"
                    />
                  </div>

                  <div className="share-form-group">
                    <label htmlFor="mobileNumber">Mobile Number*</label>
                    <div className="input-group">
                      <Field name="countryCode">
                        {({ field, form }) => (
                          <CountrySelectReact
                            value={field.value || ""}
                            onChange={val => form.setFieldValue("countryCode", val)}
                            maxWidth={120}
                            placeholder="Code"
                          />
                        )}
                      </Field>
                      <Field
                        type="text"
                        name="mobile"
                        className={`form-control ${errors.mobile && touched.mobile ? "is-invalid" : ""}`}
                        placeholder="Enter Mobile Number"
                        maxLength={10}
                      />
                    </div>
                    <ErrorMessage name="countryCode" component="div" className="error-text" />
                    <ErrorMessage name="mobile" component="div" className="error-text" />
                  </div>
                </div>

                <div className="share-form-group">
                  <label htmlFor="relation">Notes</label>
                  <Field
                    as="textarea"
                    id="notes"
                    name="notes"
                    className="form-control"
                  >
                  </Field>
                  <ErrorMessage
                    name="notes"
                    component="div"
                    className="error-text"
                  />
                </div>

                <div className="share-form-actions">
                  <button type="button" className="share-btn-cancel" onClick={onClose}>
                    <i className="bi bi-arrow-left"></i>
                    Cancel
                  </button>
                  <button type="submit" className="share-btn-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                    <i className="bi bi-arrow-right"></i>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  };

  // Success Modal Component
  const SuccessModal = ({ show, onClose, title, message }) => {
    useEffect(() => {
      if (show) {
        const timer = setTimeout(() => {
          onClose();
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [show, onClose]);

    if (!show) return null;

    return (
      <div className="share-modal-overlay" onClick={onClose}>
        <div className="share-success-modal" onClick={(e) => e.stopPropagation()}>
          <button className="share-modal-close-btn" onClick={onClose}>
            <i className="bi bi-x"></i>
          </button>

          <div className="share-success-icon">
            <div className="share-success-circle">
              <i className="bi bi-check"></i>
            </div>
          </div>

          <h2 className="share-success-title">{title}</h2>
          <p className="share-success-message">{message}</p>

          <div className="share-success-actions">
            <button className="share-btn-back" onClick={onClose}>
              <i className="bi bi-arrow-left"></i>
              Back
            </button>
            <button className="share-btn-view-collaborators" onClick={() => { onClose(); navigate('/collaborators?tab=partial-access'); }}>
              View Collaborators
            </button>
          </div>

          <p className="share-redirect-text">Redirect you to profile in 5 sec.</p>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container-fluid px-0">
        <div className="assets-liability-dashboard">
            <div className="row mb-2">
            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card">
                <div className="metric-icon">
                  <i className="bi bi-coin"></i>
                </div>
                <div className="metric-content">
                  <div className="metric-label"><h6>Total Liabilities</h6></div>
                  <div className="metric-value"><h4 title={`${getCurrencySymbol(summaryCurrency)} ${formatCurrency(liabilitiesSummary?.totalOutstanding, summaryCurrency)}`}>{getCurrencySymbol(summaryCurrency)} {formatCurrency(liabilitiesSummary?.totalOutstanding, summaryCurrency)}</h4></div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card top-performer">
                <div className="metric-icon">
                  <i className="bi bi-bar-chart-line"></i>
                </div>
                <div className="metric-content">
                  <div className="metric-label"><h6>Highest Debt</h6></div>
                  <div className="metric-value"><h4 title={`${getCurrencySymbol(summaryCurrency)} ${formatCurrency(liabilitiesSummary?.highestDebt || 0, summaryCurrency)}`}>{getCurrencySymbol(summaryCurrency)} {formatCurrency(liabilitiesSummary?.highestDebt || 0, summaryCurrency)}</h4></div>
                </div>
                <div className="external-link-icon">
                  <i className="bi bi-arrow-up-right-circle"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card">
                <div className="metric-icon">
                  <i className="bi bi-graph-up-arrow"></i>
                </div>
                <div className="metric-content">
                  <div className="metric-label"><h6>Payoff Progress</h6></div>
                  <div className="metric-value"><h4 title={`${liabilitiesSummary?.payoffProgress ? liabilitiesSummary?.payoffProgress : '0'}%`}>{liabilitiesSummary?.payoffProgress ? liabilitiesSummary?.payoffProgress : '0'}%</h4></div>
                </div>
                <div className="external-link-icon">
                  <i className="bi bi-arrow-up-right-circle"></i>
                </div>
              </div>
            </div>

            <div className="col-xl-3 col-lg-6 col-md-6 mb-3">
              <div className="metric-card">
                <div className="metric-icon">
                  <i className="bi bi-cash-stack"></i>
                </div>
                <div className="metric-content">
                  <div className="metric-label"><h6 title="Upcoming Payments">Upcoming Payments</h6></div>
                  <div className="metric-value"><h4 title={`${getCurrencySymbol(summaryCurrency)} ${formatCurrency(liabilitiesSummary?.upcomingPayments || 0, summaryCurrency)}`}>{getCurrencySymbol(summaryCurrency)} {formatCurrency(liabilitiesSummary?.upcomingPayments || 0, summaryCurrency)}</h4></div>
                </div>
              </div>
            </div>
          </div>
          {/* Filter Bar */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="filter-bar">
                <div className="filter-left">
                  {/* Liability Type Filters */}
                  <div className="items-filters">
                    <button className="filter-btn active">
                      All Liabilities <span className="filter-count">{listMeta.totalResults}</span>
                    </button>
                  </div>
                </div>

                <div className="filter-right">
                  {/* Search Input */}
                  <div className="search-container">
                    <i className="bi bi-search search-icon"></i>
                    <input
                      type="text"
                      className="form-control search-input"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Value Visibility Toggle */}
                  {/* <button
                    className="action-btn"
                    onClick={() => setIsValueVisible(!isValueVisible)}
                    title={isValueVisible ? "Hide values" : "Show values"}
                  >
                    <i className={`bi ${isValueVisible ? "bi-eye" : "bi-eye-slash"}`}></i>
                  </button> */}

                  {/* View Mode Toggle */}
                  <button
                    className="action-btn"
                    onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
                    title={`Switch to ${viewMode === "grid" ? "list" : "grid"} view`}
                  >
                    <i className={`bi ${viewMode === "grid" ? "bi-list-stars" : "bi-grid-3x3-gap"}`}></i>
                  </button>

                  {/* Share Button */}
                  <button
                    className={`action-btn share-btn ${isShareMode ? 'active' : ''}`}
                    onClick={handleShareClick}
                    title="Share Liabilities"
                  >
                    <i className="bi bi-share"></i>
                  </button>

                  {/* Export Button */}
                  <button className="action-btn export-btn" onClick={exportToExcel} title="Export All">
                    <i className="bi bi-download"></i>
                    Export All
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Share Collaborator Selection Section */}
          {isShareMode && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="share-collaborator-section">
                  <h5 className="share-collaborator-title">Select Collaborator</h5>
                  <div className="share-collaborator-selection-wrapper">
                    <Select
                      className="w-100"
                      classNamePrefix="react-select"
                      options={collaborators.map((collab) => ({
                        value: collab.id || collab._id || collab.collaboratorId,
                        label: `${collab.name || `${collab.firstName || ''} ${collab.lastName || ''}`.trim()} - ${collab.email || collab.phone || ''}`
                      }))}
                      value={collaborators
                        .filter(c => (c.id || c._id || c.collaboratorId) === selectedCollaborator)
                        .map(c => ({
                          value: c.id || c._id || c.collaboratorId,
                          label: `${c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim()} - ${c.email || c.phone || ''}`
                        }))[0] || null}
                      onChange={(option) => handleCollaboratorSelect(option ? option.value : null)}
                      placeholder="Select Collaborator"
                      isClearable
                      styles={{
                        control:     (base) => ({ ...base, fontSize: '0.875rem' }),
                        placeholder: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
                        option:      (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
                        singleValue: (base) => ({ ...base, fontSize: '0.875rem', fontWeight: 400 }),
                      }}
                    />
                    <span className="share-select-divider">or</span>
                    <button
                      className="share-add-collaborator-btn"
                      onClick={() => setShowAddCollaboratorModal(true)}
                    >
                      + Add New Collaborator
                    </button>
                  </div>

                  <div className="share-select-all-wrapper">
                    <label className="share-select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedLiabilities.length === getShareableLiabilities().length && getShareableLiabilities().length > 0}
                        onChange={handleSelectAll}
                      />
                      <span>Select All Liabilities ({getShareableLiabilities().length})</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loader */}
          {loading && <Loader />}
          {/* Error State */}
          {error && (
            <div className="error-state">
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </div>
          )}

          {/* Liabilities Display */}
          {viewMode === "grid" ? (
            /* Grid View */
            <div className="row">
              {liabilities.map((liability) => {
                const isAlreadyShared = isShareMode && selectedCollaborator && isLiabilityAlreadyShared(liability.id);
                const isShareSelectionDisabled = isLiabilityShareSelectionDisabled(liability);
                
                return (
                <div key={liability.id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                  <label 
                    htmlFor={liability.id} 
                    className={`item-card ${isShareMode && selectedLiabilities.includes(liability.id) ? 'share-selected' : ''} ${isAlreadyShared ? 'share-disabled' : ''}`}
                    style={isAlreadyShared ? { opacity: 0.5 } : {}}
                  >
                    <div className="item-card-body">
                      <div className="item-card-header">
                        <div className="item-actions-top">
                          <button
                            className="action-icon duplicate-btn"
                            onClick={() => duplicateLiability(liability.id)}
                            title="Duplicate"
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className="bi bi-files"></i>
                          </button>
                          <button
                            className={`action-icon favorite-btn ${liability.isFavorite ? "active" : ""}`}
                            onClick={() => toggleFavorite(liability.id)}
                            title={liability.isFavorite ? "Remove from favorites" : "Add to favorites"}
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className={`bi ${liability.isFavorite ? "bi-star-fill" : "bi-star"}`}></i>
                          </button>
                          <button
                            className="action-icon delete-btn"
                            onClick={() => handleDeleteClick(liability.id)}
                            title="Delete"
                            disabled={isAlreadyShared}
                            style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                        <div className="item-card-title-row">
                          {isShareMode && (
                            <div className={`share-asset-checkbox-wrapper${liability.isDraft ? ' share-draft-disabled' : ''}`}>
                              <input
                                type="checkbox"
                                id={liability.id}
                                checked={selectedLiabilities.includes(liability.id)}
                                onChange={() => handleLiabilitySelection(liability.id)}
                                className="share-asset-checkbox"
                                disabled={isShareSelectionDisabled}
                              />
                            </div>
                          )}
                          <TruncatedTooltipText
                            text={liability?.name || ""}
                            className="item-card-title-value"
                            Tag="h5"
                            onlyWhenTruncated
                          />
                        </div>
                      </div>
                      <div className="item-badges">
                        <span className="category-badge">
                          <i className="bi bi-tag"></i>
                          {formatLiabilityType(liability.liabilityType)}
                        </span>
                      </div>
                      <div className="item-value"><h4>{getCurrencySymbol(liability?.currency)} {formatCurrency(liability.outstandingAmount || liability.value, liability?.currency)}</h4></div>
                     <div>
                        {isAlreadyShared && (
                          <div className="already-shared-badge">
                            <i className="bi bi-check-circle"></i>
                            Shared
                          </div>
                        )}
                     </div>
                      <div className="item-stats">
                        <div className="stat-left">
                          {/* <div className="stat-item pill active stat-status">
                            <i className="bi bi-circle-fill"></i>
                            <span>Active</span>
                          </div> */}
                          <div className="stat-item pill">
                            <i className="bi bi-paperclip"></i>
                            <span>{((liability.images?.length || 0) + (liability.documents?.length || 0))} Attachments</span>
                          </div>
                          {liability.isDraft && (
                            <div className="stat-item pill">
                              <span className="draft-badge">
                                <i className="bi bi-pencil"></i>
                                Draft
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="stat-right">
                          <div className="stat-item">
                            <i className="bi bi-clock"></i>
                            <span>Updated {formatUpdatedAt(liability.updatedAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Edit and View buttons in footer - disabled when already shared */}
                    <div className="item-card-footer" style={isAlreadyShared ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                      <button 
                        className="btn btn-outline-primary btn-sm" 
                        disabled={isAlreadyShared}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/my-liability/${liability.id}`, {
                            state: { edit: true }
                          });
                        }}
                      >
                        <i className="bi bi-pencil"></i>
                        {liability.isDraft ? "Edit Draft" : "Edit"}
                      </button>
                      <button 
                        className="btn btn-primary btn-sm" 
                        disabled={isAlreadyShared}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/my-liability/${liability.id}`, {
                            state: { edit: false }
                          });
                        }}
                      >
                        View
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    </div>
                  </label>
                </div>
              )}
              )}
            </div>
          ) : (
            /* List View */
            <div className="items-table-container">
              <div className="table-responsive">
                <table className="table items-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Outstanding</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liabilities.map((liability) => {
                      const isAlreadyShared = isShareMode && selectedCollaborator && isLiabilityAlreadyShared(liability.id);
                      const isShareSelectionDisabled = isLiabilityShareSelectionDisabled(liability);
                      
                      return (
                      <tr key={liability.id} className={`${isShareMode && selectedLiabilities.includes(liability.id) ? 'share-selected' : ''} ${isAlreadyShared ? 'share-disabled' : ''}`}
                        onClick={(e) => {
                          const tag = e.target.tagName.toLowerCase();
                          if (tag === "button" || tag === "input" || e.target.closest(".table-actions")) return;

                          if (isShareMode && !isShareSelectionDisabled) {
                            handleLiabilitySelection(liability.id);
                          }
                        }}
                      >
                        <td>
                          <div className="item-name-cell">
                            {isShareMode ? (
                              <>
                                <input
                                  type="checkbox"
                                  checked={selectedLiabilities.includes(liability.id)}
                                  onChange={() => handleLiabilitySelection(liability.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`share-asset-checkbox-list${liability.isDraft ? ' share-draft-disabled' : ''}`}
                                  disabled={isShareSelectionDisabled}
                                />
                                {isAlreadyShared && (
                                  <span className="already-shared-badge-list" title="Already shared with this collaborator">
                                    <i className="bi bi-check-circle"></i>
                                  </span>
                                )}
                              </>
                            ) : (
                              <button
                                className={`favorite-btn-small ${liability.isFavorite ? "active" : ""}`}
                                onClick={() => toggleFavorite(liability.id)}
                              >
                                <i className={`bi ${liability.isFavorite ? "bi-star-fill" : "bi-star"}`}></i>
                              </button>
                            )}
                            <div className="item-info">
                              <div className="name">{liability.name}</div>
                              <div className="meta">
                                {liability.isDraft && <span className="draft-text">Draft</span>}
                                <span className="updated-text">Updated {formatUpdatedAt(liability.updatedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textTransform: "capitalize" }}>{formatLiabilityType(liability.liabilityType)}</td>
                        <td className="value-cell">{getCurrencySymbol(liability?.currency)} {formatCurrency(liability.outstandingAmount || liability.value, liability?.currency)}</td>
                        <td>
                          <span className="active">Active</span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="action-icon duplicate-btn"
                              onClick={() => duplicateLiability(liability.id)}
                              title="Duplicate"
                            >
                              <i className="bi bi-files"></i>
                            </button>
                            <button className="action-icon edit-btn" title="Edit" 
                              onClick={() =>
                                navigate(`/my-liability/${liability.id}`, {
                                  state: { edit: true }
                                })}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="action-icon view-btn" title="View" onClick={() =>
                              navigate(`/my-liability/${liability.id}`, {
                                state: { edit: false }
                              })}>
                              <i className="bi bi-arrow-right"></i>
                            </button>
                            <button className="action-icon delete-btn" title="Delete" onClick={() => handleDeleteClick(liability.id)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && liabilities.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="bi bi-inbox"></i>
              </div>
              <h5>No liabilities found</h5>
              {/* Only suggest changing the search when one is active; a new user just has nothing yet (ML-21) */}
              <p>{searchTerm && searchTerm.trim()
                ? "Try adjusting your search or filter criteria"
                : "Add your first liability with the + button to start tracking it."}</p>
            </div>
          )}

          {/* Share Actions Bar */}
          {isShareMode && selectedLiabilities.length > 0 && (
            <div className="share-actions-bar">
              <button className="share-btn-cancel-share" onClick={handleShareClick}>
                <i className="bi bi-arrow-left"></i>
                Cancel
              </button>
              <button className="share-btn-share-now" onClick={handleShareLiabilities}>
                Share Now
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && liabilities.length > 0 && (
            <div className="pagination-controls pagination-sticky" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button
                className="btn-sm pagination-text"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pg) => (
                <button
                  key={pg}
                  className={`btn btn-sm paginations-list ${pg === page ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handlePageChange(pg)}
                  disabled={pg === page}
                >
                  {pg}
                </button>
              ))}
              <button
                 className="btn-sm pagination-text"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Next
              </button>
              <span style={{ alignSelf: 'center', fontWeight: 500, marginLeft: 12 }}>
                of {totalPages} pages
              </span>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <ConfirmModal
            show={deleteModal.show}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            loading={deleteLoading}
            title="Delete Liability"
            message="Are you sure you want to delete this liability?"
          />
        </div>

        {/* Add Collaborator Modal */}
        <AddCollaboratorModal
          show={showAddCollaboratorModal}
          onClose={() => setShowAddCollaboratorModal(false)}
          onSubmit={async (payload, formikHelpers) => {
            try {
              const response = await api.post('/sharing/collaborators', payload, { headers: getAuthHeaders() });
              if (response && response.data) {
                // Fetch updated list after successful add
                const listRes = await api.get('/sharing/collaborators?status=active&limit=20&page=1', { headers: getAuthHeaders() });
                let collabList = Array.isArray(listRes.data?.results)
                  ? listRes.data.results.map(item => ({
                      id: item.collaboratorId?.id,
                      name: item.collaboratorId?.name || "",
                      email: item.collaboratorId?.email || "",
                      mobileNumber: item.collaboratorId?.mobileNumber || "",
                      firstName: item.collaboratorId?.firstName || "",
                      lastName: item.collaboratorId?.lastName || ""
                    }))
                  : [];
                setCollaborators(collabList);
                setNewCollaboratorName(payload.name);
                setShowAddCollaboratorModal(false);
                setShowCollaboratorAddedModal(true);
                formikHelpers.resetForm();
                showSuccessToast('Collaborator added successfully!');
              }
            } catch (e) {
              // Handle duplicate collaborator error (409) specially
              if (e?.response?.status === 409) {
                showErrorToast('This collaborator has already been added.');
              } else {
                showErrorToast(e?.message || 'Failed to add collaborator.');
              }
            } finally {
              formikHelpers.setSubmitting(false);
            }
          }}
        />

        {/* Collaborator Added Success Modal */}
        <SuccessModal
          show={showCollaboratorAddedModal}
          onClose={() => setShowCollaboratorAddedModal(false)}
          title="Collaborator Added Successfully!"
          message={`We have added ${newCollaboratorName} as Collaborator with full access to your Profile. Now they can access all your liabilities`}
        />

        {/* Liabilities Shared Success Modal */}
        <SuccessModal
          show={showLiabilitiesSharedModal}
          onClose={() => setShowLiabilitiesSharedModal(false)}
          title={`${sharedLiabilitiesCount} Liabilities Shared Successfully!`}
          message={`We have shared liabilities to ${collaborators.find(c => c.id === selectedCollaborator)?.firstName || 'Collaborator'} ${collaborators.find(c => c.id === selectedCollaborator)?.lastName || ''} with partial access to your liabilities. Now they can view your shared liabilities.`}
        />
      </div>
    </Layout>
  )
}

export default MyLiability
